import { useEffect, useState } from 'react'
import { useDispatch,useSelector } from 'react-redux';
import axios from 'axios';
import i18next from 'i18next';

import {setCurrentCompany} from '../store/currentCompanySlice.js'
import {setCurrentBranch} from '../store/currentBranchSlice.js'
import {getCompanyById} from '../utils/tenantManager.js'
import {setSettings} from '../store/settingSlice.js'
import {setLang} from '../store/langSlice.js'

import {arrayNameByLang,arrayToObjKeyValue, appStorageKey} from '../utils/global.js'
import useLicense from './useLicense.js';

const CENTRAL_SETTINGS_SCOPE = 'global';
const CENTRAL_SELECTION_SCOPE = 'header-selection';
const CENTRAL_SETTINGS_CACHE_PREFIX = 'centralSettingsCache:';
const CENTRAL_SELECTION_CACHE_PREFIX = 'centralSelectionCache:';

const useStartup = () => {
    
    const dispatch = useDispatch();
    const [startup,setStartup] = useState(false);
    const user = useSelector(state => state.auth).user
    const lang = useSelector(state => state.lang) 

    const safeParse = (value) => {
        if (!value) return null;
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    };
    const userSettings = arrayToObjKeyValue(safeParse(localStorage.getItem('user'))?.userSettings);
    const normalizeLang = (value) => String(value || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
    const storedPreferredLang = (() => {
        try {
            return localStorage.getItem('preferredLang') || localStorage.getItem('i18nextLng') || localStorage.getItem('appLang');
        } catch {
            return null;
        }
    })();
    const preferredLang = storedPreferredLang ? normalizeLang(storedPreferredLang) : null;
    const [centralSettings, setCentralSettings] = useState(null);
    const [centralLang, setCentralLang] = useState(null);
    const [centralSelection, setCentralSelection] = useState(null);
    const [selectionLoaded, setSelectionLoaded] = useState(false);
    const getNameByLang = (nameList, preferredLangValue) => {
        if (!Array.isArray(nameList)) return '';
        return (
            arrayNameByLang(nameList, preferredLangValue)?.text ||
            arrayNameByLang(nameList, preferredLangValue === 'en' ? 'ar' : 'en')?.text ||
            arrayNameByLang(nameList, 'ar')?.text ||
            arrayNameByLang(nameList, 'en')?.text ||
            nameList?.[0]?.text ||
            ''
        );
    };
    const setSelectionStorage = (key, payload) => {
        if (!payload?.value) return;
        localStorage.setItem(appStorageKey(key), JSON.stringify({
            name: payload.name,
            value: payload.value,
            updatedAt: Date.now()
        }));
    };
    const userCompany = getCompanyById(user?.company_id)
    const storedCompany = safeParse(localStorage.getItem(appStorageKey('selectedCompany')));
    const storedCompanyId = storedCompany?.value;
    const storedCompanyValid = storedCompanyId && getCompanyById(storedCompanyId);
    const centralCompanyId = centralSelection?.companyId;
    const centralCompany = centralCompanyId ? getCompanyById(centralCompanyId) : null;
    const selectedCompany = centralCompany || storedCompanyValid || userCompany;
    const selectedCompanyName = selectedCompany && (
        arrayNameByLang(selectedCompany?.name, lang.value)?.text || 
        arrayNameByLang(selectedCompany?.name, lang.value === 'en' ? 'ar' : 'en')?.text ||
        i18next.t('common.company')
    );

    const userBranch = userCompany?.branches?.find((branch) => branch?._id === user?.branch_id);
    const storedBranch = safeParse(localStorage.getItem(appStorageKey('selectedBranch')));
    const storedBranchId = storedBranch?.value;
    const companyBranches = selectedCompany?.branches || [];
    const storedBranchValid = storedBranchId && companyBranches?.some((branch) => branch?._id === storedBranchId);
    const centralBranchId = centralSelection?.branchId;
    const centralBranch = centralBranchId
        ? companyBranches?.find((branch) => branch?._id === centralBranchId)
        : null;
    const selectedBranch = centralBranch
        ? centralBranch
        : (storedBranchValid
            ? companyBranches?.find((branch) => branch?._id === storedBranchId)
            : (selectedCompany?._id === userCompany?._id ? userBranch : companyBranches?.[0]));
    const selectedBranchName = selectedBranch && (
        arrayNameByLang(selectedBranch?.name, lang.value)?.text || 
        arrayNameByLang(selectedBranch?.name, lang.value === 'en' ? 'ar' : 'en')?.text ||
        i18next.t('common.branch')
    );

    const invoiceSettings = selectedCompany && arrayToObjKeyValue(selectedCompany?.settings.invoice);
    const generalSettings = selectedCompany && arrayToObjKeyValue(selectedCompany?.settings.generalSettings);
    const mergedSettings = {
        invoiceSettings: centralSettings?.invoiceSettings || invoiceSettings,
        generalSettings: centralSettings?.generalSettings || generalSettings
    };
    const DEFAULT_LANG = mergedSettings?.generalSettings?.DEFAULT_LANG || userSettings?.DEFAULT_LANG || 'en';
    
    const {data:dataLicense} = useLicense();

    useEffect(() =>{
        const pathArray = window.location.pathname.split('/');
        if(pathArray.length > 2 && pathArray[pathArray.length-2] === 'token'){
            setStartup(true)
        }
    },[dataLicense]);
    
    useEffect(()=>{
        if(selectedCompany && selectionLoaded){
            const langFromState = lang?.value ? normalizeLang(lang.value) : null;
            const langFromStorage = preferredLang ? normalizeLang(preferredLang) : null;
            const langFromCentral = centralLang ? normalizeLang(centralLang) : null;
            const langToApply = langFromState || langFromStorage || langFromCentral || normalizeLang(DEFAULT_LANG);
            dispatch(setLang({
                label: langToApply === 'ar' ? i18next.t('language.ar') : langToApply === 'en' ? i18next.t('language.en') : langToApply,
                value: langToApply
            }))
            i18next.changeLanguage(langToApply);
            dispatch(setCurrentCompany({
                name:selectedCompanyName,
                value:selectedCompany._id
            }));
    
            dispatch(setCurrentBranch({
                name: selectedBranchName || i18next.t('common.selectBranch'),
                value: selectedBranch?._id
            }));
    
            dispatch(setSettings({
                invoiceSettings: mergedSettings.invoiceSettings,
                generalSettings: mergedSettings.generalSettings
            }));
    
            setStartup(true)
        }

    },[user, selectedCompany?._id, selectedBranch?._id, centralSettings, centralLang, lang?.value, selectionLoaded]);

    useEffect(() => {
        const fetchCentralSettings = async () => {
            if (!selectedCompany?._id || !user?.tenant_id) return;
            const token = localStorage.getItem('token');
            if (!token) return;
            const cacheKey = `${CENTRAL_SETTINGS_CACHE_PREFIX}${user.tenant_id}:${selectedCompany._id}:${CENTRAL_SETTINGS_SCOPE}`;
            const cachedRaw = safeParse(localStorage.getItem(cacheKey));
            if (cachedRaw?.data) {
                setCentralSettings(cachedRaw.data);
            }
            const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&companyId=${selectedCompany._id}&scope=${CENTRAL_SETTINGS_SCOPE}`;
            try {
                const headers = {
                    Authorization: `Bearer ${token}`
                };
                if (cachedRaw?.version) {
                    headers['If-None-Match'] = String(cachedRaw.version);
                }
                const response = await axios.get(url, { headers, validateStatus: () => true });
                if (response.status === 304) {
                    return;
                }
                if (response?.data?.status === 1) {
                    const nextData = response?.data?.data || null;
                    const nextVersion = response?.data?.version ?? cachedRaw?.version ?? 0;
                    setCentralSettings(nextData);
                    localStorage.setItem(cacheKey, JSON.stringify({ data: nextData, version: nextVersion }));
                }
            } catch (error) {
            }
        };
        fetchCentralSettings();
    }, [selectedCompany?._id, user?.tenant_id]);

    useEffect(() => {
        const fetchCentralSelection = async () => {
            if (!user?.tenant_id) {
                setSelectionLoaded(true);
                return;
            }
            const token = localStorage.getItem('token');
            if (!token) {
                setSelectionLoaded(true);
                return;
            }
            const cacheKey = `${CENTRAL_SELECTION_CACHE_PREFIX}${user.tenant_id}:${CENTRAL_SELECTION_SCOPE}`;
            const cachedRaw = safeParse(localStorage.getItem(cacheKey));
            if (cachedRaw?.data) {
                const cachedSelection = cachedRaw.data;
                setCentralLang(cachedSelection?.selectedLang || cachedSelection?.lang || cachedSelection?.language || null);
                setCentralSelection({
                    companyId: cachedSelection?.selectedCompanyId || cachedSelection?.companyId || null,
                    branchId: cachedSelection?.selectedBranchId || cachedSelection?.branchId || null
                });
                setSelectionLoaded(true);
            }
            const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&scope=${CENTRAL_SELECTION_SCOPE}`;
            try {
                const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true });
                if (response?.data?.status !== 1) {
                    setCentralLang(null);
                    return;
                }
                const selection = response?.data?.data;
                const selectedLang = selection?.selectedLang || selection?.lang || selection?.language || response?.data?.currentLanguage;
                const selectedCompanyId = selection?.selectedCompanyId || selection?.companyId;
                const selectedBranchId = selection?.selectedBranchId || selection?.branchId;
                localStorage.setItem(cacheKey, JSON.stringify({
                    data: {
                        ...selection,
                        selectedLang,
                        selectedCompanyId,
                        selectedBranchId
                    },
                    updatedAt: Date.now()
                }));
                setCentralLang(selectedLang || null);
                setCentralSelection({
                    companyId: selectedCompanyId || null,
                    branchId: selectedBranchId || null
                });
                const langToUse = normalizeLang(selectedLang || lang?.value || DEFAULT_LANG);
                if (selectedCompanyId) {
                    const companyName = response?.data?.currentCompany?.name
                        ? getNameByLang(response.data.currentCompany.name, langToUse)
                        : getNameByLang(getCompanyById(selectedCompanyId)?.name, langToUse);
                    setSelectionStorage('selectedCompany', {
                        name: companyName,
                        value: selectedCompanyId
                    });
                    if (selectedCompanyId !== selectedCompany?._id) {
                        dispatch(setCurrentCompany({
                            name: companyName,
                            value: selectedCompanyId
                        }));
                    }
                }
                if (selectedBranchId) {
                    const companyForBranch = selectedCompanyId
                        ? getCompanyById(selectedCompanyId)
                        : selectedCompany;
                    const branchEntity = companyForBranch?.branches?.find((branch) => branch?._id === selectedBranchId);
                    const branchName = response?.data?.currentBranch?.name
                        ? getNameByLang(response.data.currentBranch.name, langToUse)
                        : getNameByLang(branchEntity?.name, langToUse);
                    setSelectionStorage('selectedBranch', {
                        name: branchName,
                        value: selectedBranchId
                    });
                    if (selectedBranchId !== selectedBranch?._id) {
                        dispatch(setCurrentBranch({
                            name: branchName || i18next.t('common.branch'),
                            value: selectedBranchId
                        }));
                    }
                }
            } catch (error) {
                setCentralLang(null);
            } finally {
                setSelectionLoaded(true);
            }
        };
        fetchCentralSelection();
    }, [user?.tenant_id, user?.company_id]);

    return {startup}
}

export default useStartup
